/**
 * De bovenrand van de werkruimte: waar ben ik, is mijn werk veilig, hoe ziet
 * het eruit, en hoe kijk ik ernaar op de echte website.
 *
 * Bewust rustig: geen werkbalk vol pictogrammen, maar precies de vier dingen
 * die tijdens het bouwen voortdurend relevant zijn.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Monitor, Redo2, Smartphone, Tablet, Undo2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { SaveStatus, ViewportKey, WebsiteDetail } from '../types';
import type { DesktopViewportWidth } from '../canvasZoom';
import { ProductionPreviewButton } from './ProductionPreviewButton';
import { SaveIndicator } from './SaveIndicator';

const VIEWPORTS: { key: ViewportKey; label: string; icon: typeof Monitor }[] = [
  { key: 'desktop', label: 'Desktop', icon: Monitor },
  { key: 'tablet', label: 'Tablet', icon: Tablet },
  { key: 'mobile', label: 'Mobiel', icon: Smartphone },
];

interface BuilderTopBarProps {
  website: WebsiteDetail | null;
  pageTitle: string | null;
  saveStatus: SaveStatus;
  saveError: string | null;
  viewport: ViewportKey;
  onViewportChange: (viewport: ViewportKey) => void;
  desktopViewportWidth: DesktopViewportWidth;
  onDesktopViewportWidthChange: (width: DesktopViewportWidth) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onFlush: () => Promise<void>;
  onPreview: () => Promise<void>;
}

export function BuilderTopBar({
  website,
  pageTitle,
  saveStatus,
  saveError,
  viewport,
  onViewportChange,
  desktopViewportWidth,
  onDesktopViewportWidthChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onFlush,
  onPreview,
}: BuilderTopBarProps) {
  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-3 sm:px-5">
      <Link
        to="/sites"
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="hidden sm:inline">Sites</span>
      </Link>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 className="truncate text-sm font-bold text-slate-900">
            {website?.name ?? 'Website'}
          </h1>
          {pageTitle ? (
            <>
              <span className="text-slate-300">/</span>
              <span className="truncate text-sm text-slate-500">{pageTitle}</span>
            </>
          ) : null}
        </div>
        <div className="mt-0.5">
          <SaveIndicator status={saveStatus} errorMessage={saveError} onRetry={onFlush} />
        </div>
      </div>

      <div className="hidden items-center gap-0.5 rounded-xl border border-slate-200 p-0.5 md:flex">
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Ongedaan maken"
          title="Ongedaan maken"
          className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Undo2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onRedo}
          disabled={!canRedo}
          aria-label="Opnieuw uitvoeren"
          title="Opnieuw"
          className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Redo2 className="h-4 w-4" />
        </button>
      </div>

      <div
        role="group"
        aria-label="Weergave"
        className="hidden items-center gap-0.5 rounded-xl border border-slate-200 p-0.5 lg:flex"
      >
        {VIEWPORTS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => onViewportChange(key)}
            aria-pressed={viewport === key}
            title={label}
            data-testid={`sites-viewport-${key}`}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition',
              viewport === key
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
            )}
            >
            <Icon className="h-4 w-4" />
            <span className="hidden xl:inline">{label}</span>
          </button>
        ))}
        {viewport === 'desktop' ? (
          <select aria-label="Desktop viewportbreedte" data-testid="sites-desktop-viewport-width" value={desktopViewportWidth} onChange={(event) => onDesktopViewportWidthChange(Number(event.target.value) as DesktopViewportWidth)} className="mr-1 rounded-lg border-0 bg-transparent px-1.5 py-1.5 text-xs font-semibold text-slate-600 outline-none hover:bg-slate-100">
            <option value={1280}>Desktop · 1280 px</option>
            <option value={1440}>Desktop · 1440 px</option>
            <option value={1600}>Desktop · 1600 px</option>
          </select>
        ) : null}
      </div>

      {website ? (
        <ProductionPreviewButton
          websiteId={website.websiteId}
          onPreview={onPreview}
        />
      ) : null}
    </header>
  );
}
